import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/exterior-customer-quote")({
  validateSearch: (search: Record<string, unknown>) => ({ token: typeof search["token"] === "string" ? search["token"] : "" }),
  head: () => ({ meta: [
    { title: "Review your BookwithShields quote" },
    { name: "robots", content: "noindex, nofollow, noarchive" },
    { name: "referrer", content: "no-referrer" },
  ] }),
  component: CustomerQuoteResponsePage,
});

function CustomerQuoteResponsePage() {
  const { token } = Route.useSearch();
  const [decision, setDecision] = useState<"accept" | "decline">("accept");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true); setError(""); setMessage("");
    if (token.length < 30) { setError("This response link is missing or incomplete. Please open the full link from your email."); setBusy(false); return; }
    if (decision === "decline" && reason.trim().length < 5) { setError("Please tell us briefly why you are declining, or what needs changing."); setBusy(false); return; }
    try {
      const response = await fetch("/api/public/exterior-customer-quote-response", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, decision, reason: reason.trim() }),
      });
      const result = await response.json() as { ok?: boolean; message?: string; error?: string };
      if (!response.ok || !result.ok) throw new Error(result.error || "We couldn't save your response.");
      setMessage(result.message || "Your response has been sent to Shields.");
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't save your response. Please contact Shields.");
    } finally { setBusy(false); }
  }

  return <main className="flex min-h-screen items-center justify-center bg-[linear-gradient(180deg,#fbfcff_0%,#f1f5fa_100%)] px-4 py-10 text-[#071a38]">
    <section className="w-full max-w-xl rounded-3xl border border-[#d9e2ec] bg-white p-6 shadow-[0_22px_70px_-42px_rgba(7,26,56,.35)] sm:p-9">
      <p className="text-xs font-black uppercase tracking-[.2em] text-[#a47b32]">BookwithShields · Customer quote</p>
      <h1 className="mt-3 text-2xl font-black">Respond to your final quote</h1>
      <p className="mt-2 text-sm leading-6 text-[#506782]">Use the secure link from your email to accept or decline the quote. Accepting records your decision only; a booking and any payment must be confirmed separately by Shields.</p>
      {submitted ? <div role="status" className="mt-6 rounded-2xl border border-[#b7ddc8] bg-[#f0faf4] p-5 text-sm leading-6 text-[#245b3e"><strong className="block">Response received</strong>{message}</div> :
        <form className="mt-6 space-y-5" onSubmit={(event) => void submit(event)}>
          <fieldset className="space-y-3"><legend className="text-sm font-bold">Your decision</legend>
            <label className="flex items-start gap-3 rounded-xl border border-[#d9e2ec] p-4 text-sm"><input type="radio" name="decision" checked={decision === "accept"} onChange={() => setDecision("accept")} /><span><strong>Accept this quote</strong><span className="mt-1 block text-[#506782]">I agree to the stated price and scope, subject to separate booking confirmation.</span></span></label>
            <label className="flex items-start gap-3 rounded-xl border border-[#d9e2ec] p-4 text-sm"><input type="radio" name="decision" checked={decision === "decline"} onChange={() => setDecision("decline")} /><span><strong>Decline / request a change</strong><span className="mt-1 block text-[#506782]">Tell Shields what does not work for you.</span></span></label>
          </fieldset>
          {decision === "decline" && <label className="block text-sm font-bold">Reason or requested change
            <Textarea className="mt-2 min-h-28" required minLength={5} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Tell us what you would like to change…" />
          </label>}
          <p className="text-xs leading-5 text-[#65778d]">This secure link can be used once and expires after the validity period shown in your email. Do not forward it.</p>
          {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
          <Button className="min-h-12 w-full bg-[#071a38] hover:bg-[#102c52]" type="submit" disabled={busy || token.length < 30}>{busy ? "Sending response…" : decision === "accept" ? "Accept quote" : "Decline / request changes"}</Button>
        </form>}
    </section>
  </main>;
}
